/**
 * useStudentActivitiesState — les gestes de l'onglet Activités de l'élève.
 *
 * Ceux de la branche élève de `TodoListScreen.js`, et seulement eux (ni sélection, ni archivage, ni
 * menu des archives : le legacy les réserve au tuteur et au parent, `:510`, `:692`, `:1107`) :
 * - toucher une activité à faire la LANCE (`:532-548`) — par le lanceur partagé de l'espace,
 *   `useLaunchStudentActivity` (Sam s'il est ouvert, sinon l'exécution) ;
 * - toucher une activité faite la rouvre, terminée (`useOpenFinishedStudentActivity`) ;
 * - « Voir plus » ouvre les réalisations, sans `studentId` : c'est l'élève lui-même (`:827-841`) ;
 * - « Générer une activité » ouvre le parcours (`:1088-1094`, `canOpenGenerateModal` vaut vrai pour
 *   l'élève hors mode parent : la modale s'ouvre sans passer par le plafond `max_todo_activities`).
 *
 * L'état local tient en deux choses : le parcours de génération est-il ouvert, et le pli du bloc
 * « Faites ». Le legacy referme ce pli à chaque retour sur l'écran (`:124-128`) ; l'accordéon du
 * design system garde son pli en état d'interface, donc l'écran le remonte — au départ de l'onglet,
 * quand il ne se voit pas, pour ne rien remonter sous les yeux de l'élève.
 */
import { useFocusEffect, useNavigation } from "@react-navigation/native"
import { useCallback, useMemo, useReducer } from "react"
import { useSelector, useStore } from "react-redux"

import { findStudentActivity } from "../../../../adapters/student/Activities/studentActivitiesAdapter"
import { useLaunchStudentActivity } from "../../../hooks/useLaunchStudentActivity"
import { useOpenFinishedStudentActivity } from "./useOpenFinishedStudentActivity"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

export type StudentActivitiesUiState = {
    generateOpen: boolean
    /** Change à chaque départ de l'onglet : la clé qui remonte la page, pli « Faites » refermé. */
    foldKey: number
}

export type StudentActivitiesUiAction = { type: "OPEN_GENERATE" } | { type: "CLOSE_GENERATE" } | { type: "LEFT_TAB" }

export const initialStudentActivitiesUiState: StudentActivitiesUiState = { generateOpen: false, foldKey: 0 }

export function studentActivitiesUiReducer(
    state: StudentActivitiesUiState,
    action: StudentActivitiesUiAction
): StudentActivitiesUiState {
    switch (action.type) {
        case "OPEN_GENERATE":
            return state.generateOpen ? state : { ...state, generateOpen: true }
        case "CLOSE_GENERATE":
            return state.generateOpen ? { ...state, generateOpen: false } : state
        case "LEFT_TAB":
            return { ...state, foldKey: state.foldKey + 1 }
        default:
            return state
    }
}

type PushNavigation = { push: (route: string, params?: object) => void }

export function useStudentActivitiesState() {
    const navigation = useNavigation() as unknown as PushNavigation
    const store = useStore()
    const launchActivity = useLaunchStudentActivity()
    const openFinished = useOpenFinishedStudentActivity()
    const studentId = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (selectDisplayedStudent(state)?.id ?? null) as string | number | null
    )
    const [ui, dispatchUi] = useReducer(studentActivitiesUiReducer, initialStudentActivitiesUiState)

    useFocusEffect(
        useCallback(() => {
            return () => dispatchUi({ type: "LEFT_TAB" })
        }, [])
    )

    const actions = useMemo(
        () => ({
            /** Une ligne de « À faire » : l'activité brute est relue au toucher, l'exécution la lit entière. */
            launch: (id: string | number) => {
                const account = selectDisplayedStudent(store.getState())
                const activity = findStudentActivity({ todo_activities: account?.todo_activities }, id)
                if (activity != null) launchActivity(activity)
            },
            openDone: openFinished,
            seeAllDone: () => navigation.push("realizations", { initialTabRealizations: true, withGoBack: true }),
            openGenerate: () => dispatchUi({ type: "OPEN_GENERATE" }),
            closeGenerate: () => dispatchUi({ type: "CLOSE_GENERATE" }),
        }),
        [launchActivity, navigation, openFinished, store]
    )

    return { studentId, generateOpen: ui.generateOpen, foldKey: ui.foldKey, actions }
}
